<template>
  <section class="industry-layout">
    <div class="container">
      <div class="section-header">
        <h2 class="section-title fade-in-up">产业布局</h2>
        <p class="section-subtitle fade-in-up">Industrial Layout</p>
        <div class="title-decoration"></div>
      </div>
      
      <div class="industry-showcase">
        <div class="industry-card" v-for="(industry, index) in industries" :key="index" @click="handleCardClick(industry)" :style="{'--index': index}">
          <div class="card-content">
            <div class="icon-wrapper">
              <div class="custom-icon" :class="industry.iconClass"></div>
            </div>
            <h3 class="industry-title">{{ industry.title }}</h3>
            <p class="industry-subtitle">{{ industry.subtitle }}</p>
            <div class="industry-description">
              <p class="description-text">{{ industry.description }}</p>
            </div>
            <div class="application-scenarios">
              <h4 class="scenario-title">应用场景</h4>
              <ul class="scenario-list">
                <li v-for="(scenario, idx) in industry.scenarios.slice(0, 4)" :key="scenario">{{ scenario }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'IndustryLayout',
  data() {
    return {
      resizeTimer: null,
      industries: [
        {
          title: '制造业',
          subtitle: '智能制造解决方案',
          iconClass: 'icon-manufacturing',
          description: '基于工业4.0理念，提供全流程数字化制造解决方案，实现生产过程的智能化、自动化和精益化管理。',
          scenarios: ['数字工厂数据底座', '能碳综合管理系统', '设备健康监测', '供应链优化'],
        },
        {
          title: '冶金',
          subtitle: '冶金行业数字化',
          iconClass: 'icon-metallurgy',
          description: '针对钢铁、有色金属等冶金企业，提供从原料到成品的全流程数字化监控和优化解决方案。',
          scenarios: ['生产数采平台', '轧制工艺优化', '能耗监测', '设备维护'],
        },
        {
          title: '化工',
          subtitle: '化工安全智能监控',
          iconClass: 'icon-chemical',
          description: '专注化工行业安全生产和环保合规，提供全方位的智能监控、预警和应急响应系统。',
          scenarios: ['碳排放管理', '工艺安全管理', '环境监测', '设备运维'],
        },
        {
          title: '智慧园区',
          subtitle: '园区综合管理平台',
          iconClass: 'icon-park',
          description: '构建数字化、智能化的产业园区生态系统，实现园区运营管理的全面升级和服务优化。',
          scenarios: ['零碳智慧园区', '医院智慧后勤', '智慧楼宇'],
        }
      ]
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  methods: {
    handleCardClick(industry) {
      this.$message({
        message: `${industry.title}解决方案详情页面开发中...`,
        type: 'info'
      })
    },
    handleResize() {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        this.currentIndex = 0
        const carousel = this.$el.querySelector('.industry-carousel')
        if (carousel) {
          carousel.style.transform = 'translateX(0px)'
        }
      }, 200)
    }
  },
  beforeDestroy() {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
    }
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>

<style lang="scss" scoped>
.industry-layout {
  padding: 120px 0;
  background: #ffffff;
  position: relative;
  
  .container {
    max-width: 1440px;
    margin: 0 auto;
    padding: 0 40px;
    position: relative;
    z-index: 1;
  }
}

.section-header {
  text-align: center;
  margin-bottom: 80px;
  position: relative;
  
  .section-title {
    font-size: 3.5rem;
    font-weight: 900;
    color: #111827;
    margin-bottom: 20px;
    letter-spacing: -1px;
    position: relative;
  }
  
  .section-subtitle {
    font-size: 1.1rem;
    color: #6b7280;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 3px;
    margin-bottom: 36px;
    position: relative;
    
    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      width: 64px;
      height: 1px;
      background: #e5e7eb;
    }
    
    &::before { left: -88px; }
    &::after { right: -88px; }
  }
  
  .title-decoration {
    width: 120px;
    height: 2px;
    background: #3b82f6;
    margin: 0 auto;
    border-radius: 2px;
  }
}

@keyframes titleGlow {
  0% { filter: brightness(1) drop-shadow(0 0 5px rgba(59, 130, 246, 0.3)); }
  100% { filter: brightness(1.1) drop-shadow(0 0 20px rgba(59, 130, 246, 0.5)); }
}

@keyframes decorationPulse {
  0%, 100% { opacity: 0.6; transform: scaleX(1); }
  50% { opacity: 1; transform: scaleX(1.1); }
}

.industry-showcase {
  display: flex;
  gap: 24px;
  justify-content: center;
  align-items: stretch;
  
  .industry-card {
    flex: 1;
    min-width: 0;
    background: #ffffff;
    border-radius: 16px;
    padding: 40px 32px;
    box-shadow: 0 6px 24px rgba(17, 24, 39, 0.08);
    border: 1px solid #e5e7eb;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    cursor: pointer;
    opacity: 1;
    transform: none;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.04), transparent);
      transition: left 0.6s ease;
    }
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: #3b82f6;
      transform: scaleX(0);
      transform-origin: left;
      transition: transform 0.4s ease;
    }
    
    &:hover {
      transform: translateY(-6px);
      box-shadow: 0 10px 32px rgba(17, 24, 39, 0.12);
      border-color: #d1d5db;
      
      &::before {
        left: 100%;
      }
      
      &::after {
        transform: scaleX(1);
      }
      
      .icon-wrapper {
        transform: scale(1.04);
      }
      
      .industry-title {
        color: #1e3a8a;
      }
    }
    
    .card-content {
      position: relative;
      z-index: 2;
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      height: 100%;
    }
  }
}

.icon-wrapper {
  width: 80px;
  height: 80px;
  background: #f9fafb;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  transition: all 0.4s ease;
  position: relative;
  box-shadow: 0 4px 16px rgba(17, 24, 39, 0.08);
  border: 1px solid #e5e7eb;
  flex-shrink: 0;
  
  .custom-icon {
    width: 40px;
    height: 40px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    filter: none;
    
    &::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      top: 0;
      left: 0;
    }
  }
}

.icon-manufacturing::before {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3E%3Crect x='4' y='6' width='16' height='12' rx='2' fill='%23dbeafe'/%3E%3Crect x='6' y='8' width='12' height='8' rx='1' fill='%233b82f6'/%3E%3Cpath d='M9 12h6M9 14h4' stroke='%231e40af' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
}

.icon-metallurgy::before {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M4 8l8-4 8 4v8l-8 4-8-4V8z' fill='%23dbeafe'/%3E%3Cpath d='M6 9l6-3 6 3v6l-6 3-6-3V9z' fill='%233b82f6'/%3E%3Cpath d='M8 12l4-2 4 2v2l-4 2-4-2v-2z' stroke='%231e40af' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E");
}

.icon-chemical::before {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3E%3Cg stroke='%231e40af' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='4' fill='%233b82f6'/%3E%3Cpath d='M12 3v3M12 18v3M3 12h3M18 12h3M5.5 5.5l2.1 2.1M16.4 16.4l2.1 2.1M18.5 5.5l-2.1 2.1M7.6 16.4l-2.1 2.1'/%3E%3C/g%3E%3C/svg%3E");
}

.icon-building::before {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3E%3Crect x='6' y='4' width='12' height='16' rx='1' fill='%23dbeafe'/%3E%3Crect x='8' y='6' width='8' height='12' rx='0.5' fill='%233b82f6'/%3E%3Crect x='10' y='8' width='4' height='2' fill='%231e40af'/%3E%3Crect x='10' y='12' width='4' height='2' fill='%231e40af'/%3E%3C/svg%3E");
}

.icon-park::before {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M12 21s6-6 6-10a6 6 0 10-12 0c0 4 6 10 6 10z' fill='%23dbeafe' stroke='%233b82f6' stroke-width='1.5'/%3E%3Ccircle cx='12' cy='11' r='2.5' fill='%233b82f6'/%3E%3C/svg%3E");
}

@keyframes cardEntrance {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes iconPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

@keyframes shimmer {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: 200px 0;
  }
}

.industry-card {
  .industry-title {
    font-size: 2rem;
    font-weight: 700;
    color: #1e3a8a;
    margin-bottom: 12px;
    transition: color 0.3s ease;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 50%;
      width: 60px;
      height: 3px;
      background: #3b82f6;
      border-radius: 2px;
      transform: translateX(-50%);
    }
  }
  
  .industry-subtitle {
    font-size: 1rem;
    color: #6b7280;
    margin-bottom: 20px;
    font-weight: 500;
  }
  
  .industry-description {
    margin-bottom: 24px;
    
    .description-text {
      font-size: 0.95rem;
      line-height: 1.7;
      color: #374151;
      text-align: justify;
    }
  }
  
  .core-technologies {
    margin-bottom: 24px;
    
    .tech-title {
      font-size: 1rem;
      font-weight: 600;
      color: #111827;
      margin-bottom: 12px;
    }
    
    .tech-content {
      .tech-highlight {
        display: inline-block;
        background: #f3f4f6;
        color: #111827;
        padding: 8px 16px;
        border-radius: 20px;
        font-size: 0.85rem;
        font-weight: 500;
        border: 1px solid #e5e7eb;
      }
    }
  }
  
  .industry-stats {
    display: flex;
    justify-content: center;
    gap: 32px;
    margin-bottom: 24px;
    padding: 20px;
    background: #f9fafb;
    border-radius: 16px;
    border: 1px solid #e5e7eb;
    
    .stat-item {
      text-align: center;
      
      .stat-number {
        display: block;
        font-size: 2rem;
        font-weight: 800;
        color: #111827;
        margin-bottom: 4px;
      }
      
      .stat-label {
        font-size: 0.85rem;
        color: #6b7280;
        font-weight: 500;
      }
    }
  }
  
  .application-scenarios {
    .scenario-title {
      font-size: 1rem;
      font-weight: 600;
      color: #111827;
      margin-bottom: 12px;
    }
    
    .scenario-list {
      list-style: none;
      padding: 0;
      margin: 0;
      
      li {
        padding: 8px 0;
        font-size: 0.9rem;
        color: #6b7280;
        position: relative;
        padding-left: 22px;
        
        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
        width: 6px;
        height: 6px;
        transform: translateY(-50%);
        border-radius: 50%;
        background: #3b82f6;
        box-shadow: none;
      }
    }
  }
}
}

/* 响应式调整 */
@media (max-width: 1200px) {
  .industry-showcase {
    gap: 20px;
    
    .industry-card {
      padding: 28px 20px;
      
      .icon-wrapper {
        width: 70px;
        height: 70px;
        margin-bottom: 20px;
        
        .custom-icon {
          width: 35px;
          height: 35px;
        }
      }
      
      .industry-title {
        font-size: 1.8rem;
        margin-bottom: 12px;
      }
      
      .industry-subtitle {
        font-size: 0.9rem;
        margin-bottom: 20px;
      }
      
      .industry-description {
        margin-bottom: 20px;
        
        .description-text {
          font-size: 0.85rem;
        }
      }
      
      .industry-stats {
        margin-bottom: 20px;
        
        .stat-item {
          .stat-number {
            font-size: 1.8rem;
          }
          
          .stat-label {
            font-size: 0.8rem;
          }
        }
      }
    }
  }
}

@media (max-width: 992px) {
  .industry-layout {
    padding: 80px 0;
    
    .container {
      padding: 0 32px;
    }
  }
  
  .section-header {
    margin-bottom: 60px;
    
    .section-title {
      font-size: 2.8rem;
    }
    
    .section-subtitle {
      font-size: 1.1rem;
      letter-spacing: 2px;
      
      &::before,
      &::after {
        width: 40px;
      }
      
      &::before {
        left: -60px;
      }
      
      &::after {
        right: -60px;
      }
    }
  }
  
  .industry-showcase {
    flex-wrap: wrap;
    gap: 24px;
    
    .industry-card {
      flex: 0 0 calc(50% - 12px);
      min-width: 300px;
      padding: 28px 20px;
    }
  }
}

@media (max-width: 768px) {
  .industry-layout {
    padding: 60px 0;
    
    .container {
      padding: 0 24px;
    }
  }
  
  .section-header {
    margin-bottom: 50px;
    
    .section-title {
      font-size: 2.2rem;
    }
    
    .section-subtitle {
      font-size: 1rem;
      letter-spacing: 1px;
      
      &::before,
      &::after {
        display: none;
      }
    }
  }
  
  .industry-showcase {
    flex-direction: column;
    gap: 30px;
    
    .industry-card {
      flex: 0 0 auto;
      padding: 24px 18px;
      
      .icon-wrapper {
        width: 70px;
        height: 70px;
        margin-bottom: 18px;
        
        .custom-icon {
          width: 35px;
          height: 35px;
        }
      }
      
      .industry-title {
        font-size: 1.5rem;
        margin-bottom: 10px;
        
        &::after {
          width: 50px;
          height: 2px;
        }
      }
      
      .industry-subtitle {
        font-size: 0.85rem;
        margin-bottom: 18px;
      }
      
      .industry-description {
        margin-bottom: 18px;
        
        .description-text {
          font-size: 0.8rem;
        }
      }
      
      .industry-stats {
        margin-bottom: 18px;
        
        .stat-item {
          .stat-number {
            font-size: 1.5rem;
          }
          
          .stat-label {
            font-size: 0.75rem;
          }
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .industry-layout .container {
    padding: 0 16px;
  }
  
  .industry-showcase .industry-card {
    .icon-wrapper {
      width: 60px;
      height: 60px;
      
      .custom-icon {
        width: 30px;
        height: 30px;
      }
    }
    
    .industry-title {
      font-size: 1.2rem;
    }
    
    .industry-subtitle {
      font-size: 0.8rem;
    }
  }
}
</style>